/**
 * Loads frozen dataset snapshots served as static files from public/snapshots/.
 *
 * A prebuild step (package.json "prebuild") copies the repo-root snapshots/
 * directory into public/ so CRA serves them at /snapshots/*.json.
 *
 * In dev: `npm start` runs `prebuild` implicitly via the `start` script hook.
 * In prod: `npm run build` runs it before webpack.
 */

const BASE = process.env.PUBLIC_URL || "";
const SNAPSHOT_BASE_PATHS = [
  `${BASE}/snapshots`,
  `${BASE}/snapshots/snapshots`,
];

/**
 * In-memory cache so we don't re-fetch the same snapshot repeatedly.
 * Keyed by version label. The manifest is cached separately under "__manifest".
 */
const cache = new Map();

async function fetchJson(url) {
  const resp = await fetch(url, { cache: "no-cache" });
  if (!resp.ok) {
    const error = new Error(
      `Failed to fetch ${url}: ${resp.status} ${resp.statusText}`
    );
    error.status = resp.status;
    throw error;
  }

  const contentType = resp.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const error = new Error(`Expected JSON from ${url}, got ${contentType}`);
    error.status = resp.status;
    error.isSnapshotPathMiss = true;
    throw error;
  }

  try {
    return await resp.json();
  } catch (parseError) {
    parseError.status = resp.status;
    parseError.isSnapshotPathMiss = true;
    throw parseError;
  }
}

async function fetchSnapshotAsset(filename) {
  let lastError = null;
  for (const basePath of SNAPSHOT_BASE_PATHS) {
    try {
      return await fetchJson(`${basePath}/${filename}`);
    } catch (error) {
      if (error.status !== 404 && !error.isSnapshotPathMiss) {
        throw error;
      }
      lastError = error;
    }
  }
  throw lastError;
}

/**
 * Fetch the snapshots manifest listing all available dataset versions.
 * @returns {Promise<{data: {latest, updated_at, versions: [...]}, error}>}
 */
export const fetchSnapshotManifest = async () => {
  try {
    if (cache.has("__manifest")) {
      return { data: cache.get("__manifest"), error: null };
    }
    const manifest = await fetchSnapshotAsset("manifest.json");
    cache.set("__manifest", manifest);
    return { data: manifest, error: null };
  } catch (error) {
    console.error("Error fetching snapshot manifest:", error);
    return { data: null, error };
  }
};

/**
 * Fetch a specific dataset snapshot by version.
 * @param {string} version - e.g. "2.0.0"
 * @returns {Promise<{data: Object, error}>}
 */
export const fetchSnapshot = async (version) => {
  if (!version) {
    return {
      data: null,
      error: new Error("fetchSnapshot: version is required"),
    };
  }
  try {
    if (cache.has(version)) {
      return { data: cache.get(version), error: null };
    }
    const snapshot = await fetchSnapshotAsset(`v${version}.json`);
    cache.set(version, snapshot);
    return { data: snapshot, error: null };
  } catch (error) {
    console.error(`Error fetching snapshot v${version}:`, error);
    return { data: null, error };
  }
};

/**
 * Convenience: fetch the latest snapshot by consulting the manifest.
 * @returns {Promise<{data: Object, error}>}
 */
export const fetchLatestSnapshot = async () => {
  const { data: manifest, error } = await fetchSnapshotManifest();
  if (error || !manifest?.latest) {
    return {
      data: null,
      error: error || new Error("No snapshots available"),
    };
  }
  return fetchSnapshot(manifest.latest);
};

/**
 * Resolve the config row for a given curriculum/subject whose modifiers
 * match the run's filters. Mirrors worker/src/utils/snapshotLoader.js.
 *
 * @param {Object} snapshot - loaded snapshot
 * @param {string} curriculum - e.g. "common_core"
 * @param {string} subject - e.g. "math"
 * @param {Object} filters - { request_category, has_interests, has_stimulus }
 * @returns {{config, activeModifiers}|null}
 */
export const resolveConfigForFilters = (
  snapshot,
  curriculum,
  subject,
  filters
) => {
  const key = `${curriculum}/${subject}`;
  const rows = snapshot?.config?.[key];
  if (!rows || rows.length === 0) {
    return null;
  }
  const modifiersById = new Map(
    (snapshot.modifiers || []).map((m) => [m.id, m])
  );
  for (const row of rows) {
    const rowModifiers = (row.modifier_ids || [])
      .map((id) => modifiersById.get(id))
      .filter(Boolean);
    const matching = rowModifiers.filter((m) => {
      if (
        filters.request_category &&
        m.request_category !== filters.request_category
      ) {
        return false;
      }
      if (
        filters.has_interests !== undefined &&
        filters.has_interests !== null &&
        m.has_interests !== filters.has_interests
      ) {
        return false;
      }
      if (
        filters.has_stimulus !== undefined &&
        filters.has_stimulus !== null &&
        m.has_stimulus !== filters.has_stimulus
      ) {
        return false;
      }
      return true;
    });
    if (matching.length > 0) {
      return { config: row, activeModifiers: matching };
    }
  }
  return null;
};

/**
 * Flatten a snapshot's config into a BenchmarkSets-style listing.
 * Returns one item per (curriculum, subject, modifier) expanded with grades.
 *
 * Each item: { curriculum, subject, grade, request_category, has_interests, has_stimulus }
 */
export const flattenSnapshotConfig = (snapshot) => {
  if (!snapshot?.config) {
    return [];
  }
  const modifiersById = new Map(
    (snapshot.modifiers || []).map((m) => [m.id, m])
  );
  const out = [];
  for (const [key, rows] of Object.entries(snapshot.config)) {
    const [curriculum, subject] = key.split("/");
    for (const row of rows) {
      for (const modifierId of row.modifier_ids || []) {
        const modifier = modifiersById.get(modifierId);
        if (!modifier) {
          continue;
        }
        for (const grade of row.grades || []) {
          out.push({
            curriculum,
            subject,
            grade,
            request_category: modifier.request_category,
            has_interests: modifier.has_interests,
            has_stimulus: modifier.has_stimulus,
          });
        }
      }
    }
  }
  return out;
};
